import { api } from "@/lib/api";

/**
 * One item on the bell.
 *
 * ⚠️ Carries the SUBJECT LINE and no body. The server never sends one: the stored body is
 * HTML built for email clients, and putting that in front of the browser would need
 * `dangerouslySetInnerHTML`, which is banned. In-app rows are written without a body at all,
 * so there is nothing here to render unsafely even by accident.
 *
 * ⚠️ Two numbers doing different jobs (D-4, D-35). `displayNumber` is what the item reads
 * as — `#412`, the reference people quote — and `number` is the opaque value the link is
 * built on. Never route on the readable one.
 *
 * ⚠️ Both are NULL on a follow-up reminder (D-95), which is about a private row on the reader's
 * own list rather than about a request and may name no request at all. The absent `number` is
 * what tells the bell to open `/todos` — the server withholds it exactly when there is nothing
 * to route to, so the rendering cannot disagree with the payload.
 */
export interface AppNotification {
  id: string;
  number: string | null;
  displayNumber: number | null;
  subject: string;
  eventType: string;
  createdAtUtc: string;
  readAtUtc: string | null;
}

export interface NotificationList {
  items: AppNotification[];

  /** Unread across everything, not just the items above. */
  unreadCount: number;
}

export function getNotifications(): Promise<NotificationList> {
  return api<NotificationList>("/api/notifications");
}

export function markNotificationRead(id: string): Promise<void> {
  return api<void>(`/api/notifications/${id}/read`, { method: "POST" });
}

export function markAllNotificationsRead(): Promise<{ marked: number }> {
  return notificationsRead("/api/notifications/read");
}

/**
 * Clears the unread updates for one request, because it has just been read.
 *
 * ⚠️ WHAT MAKES THE HOME BADGE HONEST. Without this somebody reads every reply on a request,
 * goes back to the home screen and is still told there are three updates waiting — a badge
 * that outlives the act of reading, which teaches people to ignore it.
 *
 * ⚠️ The OPAQUE number (D-4). Never the readable one.
 *
 * ⚠️ Best effort by design. This is housekeeping on a screen that has already loaded, so a
 * failure must not surface as an error on a page somebody is reading. The worst case is the
 * badge clearing on the next visit instead of this one.
 */
export function markTicketNotificationsRead(number: string): Promise<{ marked: number }> {
  return notificationsRead(`/api/notifications/read?ticket=${encodeURIComponent(number)}`);
}

/**
 * Clears the unread follow-up reminders, because the list has just been opened (D-95).
 *
 * ⚠️ THE SAME RULE AS A REQUEST, applied to the only notification that has no request to be
 * cleared by. A reminder names no ticket, so `markTicketNotificationsRead` can never reach one —
 * without this the badge would survive the reader going to the screen, reading the note and
 * ticking it off, which is the badge-that-outlives-reading defect D-70 was written for.
 *
 * ⚠️ Best effort, like its sibling: housekeeping on a screen that has already loaded must not
 * surface as an error on a page somebody is using.
 */
export function markFollowUpNotificationsRead(): Promise<{ marked: number }> {
  return notificationsRead("/api/notifications/read?followUps=true");
}

/**
 * Posts, then tells the bell.
 *
 * The bell reloads on focus and visibility change and deliberately does not poll, so without
 * a nudge it would keep showing a count that a different part of the same page has already
 * cleared. A DOM event rather than shared state: the bell lives in the app chrome and the
 * callers are routes, and threading a context through every one of them to deliver "something
 * changed" is more machinery than the message deserves.
 */
async function notificationsRead(path: string): Promise<{ marked: number }> {
  const result = await api<{ marked: number }>(path, { method: "POST" });

  if (result.marked > 0) {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED));
  }

  return result;
}

export const NOTIFICATIONS_CHANGED = "notifications:changed";
